import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { CriteriosBuilder, emptyCrit, normalizeCrit, serializeCrits } from "@/components/CriteriosBuilder";
import { DIMENSOES } from "@/lib/constants";
import { Plus, Trash2, FileText, Pencil, Layers } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({ nome: "", descricao: "", curso_id: "" });

export default function ModelosFicha() {
  const { isAdmin } = useAuth();
  const [modelos, setModelos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [criterios, setCriterios] = useState([emptyCrit()]);

  const load = () => api.get("/modelos-ficha").then((r) => setModelos(r.data));
  useEffect(() => {
    load();
    api.get("/cursos").then((r) => setCursos(r.data));
  }, []);

  const cursoNome = (id) => cursos.find((c) => c.id === id)?.nome || "Qualquer curso";

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setCriterios([emptyCrit()]);
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m.id);
    setForm({ nome: m.nome || "", descricao: m.descricao || "", curso_id: m.curso_id || "" });
    setCriterios((m.criterios || []).length ? m.criterios.map(normalizeCrit) : [emptyCrit()]);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao,
        curso_id: form.curso_id || null,
        criterios: serializeCrits(criterios),
      };
      if (editing) {
        await api.put(`/modelos-ficha/${editing}`, payload);
        toast.success("Modelo atualizado");
      } else {
        await api.post("/modelos-ficha", payload);
        toast.success("Modelo criado");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/modelos-ficha/${id}`); toast.success("Modelo removido"); load(); };

  return (
    <div>
      <PageHeader title="Modelos de Ficha" subtitle="Fichas reutilizáveis de critérios para aplicar nas oficinas">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="novo-modelo-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo modelo</Button></DialogTrigger>
            <DialogContent className="bg-popover border-border max-w-2xl max-h-[88vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Editar modelo de ficha" : "Novo modelo de ficha"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Nome do modelo</Label><Input data-testid="modelo-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Ficha Negociação Policial" /></div>
                  <div>
                    <Label>Curso (opcional)</Label>
                    <Select value={form.curso_id || "none"} onValueChange={(v) => setForm({ ...form, curso_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="modelo-curso"><SelectValue placeholder="Qualquer curso" /></SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="none">Qualquer curso</SelectItem>
                        {cursos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Descrição</Label><Textarea data-testid="modelo-descricao" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
                <CriteriosBuilder criterios={criterios} setCriterios={setCriterios} />
              </div>
              <DialogFooter>
                <Button data-testid="salvar-modelo-btn" onClick={save} disabled={!form.nome.trim()}>{editing ? "Salvar alterações" : "Salvar modelo"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {modelos.map((m) => {
          const grupos = [...new Set((m.criterios || []).map((c) => c.grupo).filter(Boolean))];
          return (
            <Card key={m.id} data-testid={`modelo-${m.id}`} className="p-5 bg-card border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-primary/15"><FileText className="h-5 w-5 text-primary" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-lg leading-tight truncate">{m.nome}</div>
                    <div className="text-xs text-muted-foreground">{cursoNome(m.curso_id)}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`edit-modelo-${m.id}`} onClick={() => openEdit(m)} className="text-muted-foreground hover:text-primary" title="Editar modelo"><Pencil className="h-4 w-4" /></button>
                    {!m.oficial && <button data-testid={`del-modelo-${m.id}`} onClick={() => remove(m.id)} className="text-muted-foreground hover:text-red-400" title="Excluir"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                )}
              </div>
              {m.oficial && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`modelo-oficial-${m.id}`}>
                  <Badge className="bg-primary/20 text-primary border-primary/40 text-[10px] font-bold uppercase tracking-widest">Modelo Oficial</Badge>
                  <Badge className="bg-red-500/15 text-red-300 border-red-500/40 text-[10px] font-bold uppercase tracking-widest">Não deletar</Badge>
                </div>
              )}
              {m.descricao && <p className="text-sm text-muted-foreground mt-3">{m.descricao}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{(m.criterios || []).length} critérios</span>
                {m.banco_contexto === "primeiro_interventor" && <span>Banco: Primeiro Interventor</span>}
                {grupos.length > 0 && <span>{grupos.length} grupo(s)</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(m.criterios || []).slice(0, 8).map((c, i) => (
                  <Badge key={i} className={`${DIMENSOES[c.dimensao]?.badge} whitespace-normal break-words max-w-full text-left`}>{c.nome}</Badge>
                ))}
                {(m.criterios || []).length > 8 && <Badge className="bg-secondary text-muted-foreground">+{m.criterios.length - 8}</Badge>}
              </div>
            </Card>
          );
        })}
        {!modelos.length && <p className="text-muted-foreground text-sm">Nenhum modelo de ficha cadastrado.</p>}
      </div>
    </div>
  );
}
